import React, { useEffect, useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  CircularProgress,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { ROLES } from '../constants/roles';
import AutocompleteField from './inputs/AutocompleteField';
import { getClientDisplayName } from '../utils/orderItemDisplay';
import { useAsyncAction } from '../hooks/useAsyncAction';

export default function MoveItemsToClientDialog({
  open,
  onClose,
  itemIds,
  currentClientId,
  onSuccess,
  isMobile = false,
}) {
  const { t } = useTranslation();
  const [clients, setClients] = useState([]);
  const [clientsLoading, setClientsLoading] = useState(false);
  const [targetClientId, setTargetClientId] = useState('');
  const [error, setError] = useState('');
  const { loading: submitting, run: runSubmit } = useAsyncAction();

  useEffect(() => {
    if (!open) return;
    setTargetClientId('');
    setError('');
    setClientsLoading(true);
    axios.get(`/api/users?role=${encodeURIComponent(ROLES.CLIENT)}`)
      .then((res) => {
        const list = (res.data || []).filter(
          (u) => !u.disabled && String(u._id) !== String(currentClientId),
        );
        setClients(list);
      })
      .catch(() => setError(t('clients.moveToClientLoadFailed')))
      .finally(() => setClientsLoading(false));
  }, [open, currentClientId, t]);

  const handleSubmit = () => {
    if (!targetClientId || !itemIds?.length) return;
    setError('');
    runSubmit(async () => {
      try {
        const res = await axios.post('/api/order-items/transfer-client', {
          itemIds,
          targetClientId,
        });
        onSuccess?.(res.data);
        onClose?.();
      } catch (err) {
        setError(err.response?.data?.error || t('clients.moveToClientFailed'));
      }
    });
  };

  const handleClose = () => {
    if (submitting) return;
    onClose?.();
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="xs" fullWidth fullScreen={isMobile}>
      <DialogTitle>{t('clients.moveItemsToClient')}</DialogTitle>
      <DialogContent dividers>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          {t('clients.moveToClientHint')}
        </Typography>
        <Typography variant="body2" sx={{ mb: 2 }}>
          {itemIds?.length || 0} {t('clients.items').toLowerCase()}
        </Typography>
        {clientsLoading ? (
          <CircularProgress size={24} />
        ) : (
          <AutocompleteField
            options={clients}
            value={targetClientId}
            onChange={setTargetClientId}
            label={t('clients.moveToClientSelect')}
            placeholder={t('clients.moveToClientSelect')}
            getOptionValue={(opt) => opt._id}
            getOptionLabel={(opt) => getClientDisplayName(opt)}
            isOptionEqualToValue={(opt, val) => opt._id === val}
          />
        )}
        {error && (
          <Typography color="error" variant="body2" sx={{ mt: 2 }}>{error}</Typography>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} disabled={submitting}>{t('common.cancel')}</Button>
        <Button
          variant="contained"
          onClick={handleSubmit}
          disabled={!targetClientId || submitting}
        >
          {t('clients.moveToClientConfirm')}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
